/**
 * Permission config with subtabs and CRUD (create, read, update, delete).
 * Built from sidebar + subtabs (sidebarPermissionConfig) so no tab/subtab is missed.
 *
 * IMPORTANT:
 * - UI/API types: view, create, edit, delete
 * - Internal CRUD terms: read, create, update, delete
 */

import {
  SIDEBAR_PERMISSION_STRUCTURE,
  getLeafKeysFromStructure,
} from "./sidebarPermissionConfig";

/** Permission types in UI/API: view = read, create, edit = update, delete */
export const PERMISSION_TYPES = ["view", "create", "edit", "delete"];

/**
 * Keys that belong to Instalaxmi permissions.
 * Exported so it can be used in other files for consistency.
 */
export const INSTALAXMI_KEYS = new Set([
  "instalaxmiPermission",
  "jeweller",
  "subscriptionMain",
  "jewellerSubscriptionList",
  "couponlist",
  "configuration",
  "appConfig",
  "jewellerPermission",
  "training",
  "dashboardInfo",
  "leads"
]);

/**
 * Whether a CRUD permission is applicable for a given leaf key in Jeweller context.
 * true = permission is needed (show toggle); false = not needed (hide/—).
 *
 * Must include every Jeweller leaf key from SIDEBAR_PERMISSION_STRUCTURE.
 */
const JEWELLER_CRUD_OVERRIDES = {
  // Dashboard
  dashboard: { create: false, read: true, update: false, delete: false },

  // Metal Management
  editMetal: { create: true, read: true, update: true, delete: true },
  metalPriceHistory: { create: true, read: true, update: true, delete: false },

  // Transactions
  transactionHistory: { create: false, read: true, update: true, delete: false },
  sell: { create: false, read: true, update: true, delete: false },
  redeem: { create: false, read: true, update: true, delete: false },
  offlineTransaction: { create: false, read: true, update: true, delete: false },
  diamondPointsOrder: { create: false, read: true, update: true, delete: false },
  userPointsOrder: { create: false, read: true, update: true, delete: false },
  ProductOrder: { create: false, read: true, update: true, delete: false },
  newGifts: { create: false, read: true, update: true, delete: false },
  allSubscriptionList: { create: false, read: true, update: true, delete: false },

  // App Management
  customNotification: { create: true, read: true, update: true, delete: true },
  content: { create: true, read: true, update: true, delete: true },
  carouselsettings: { create: true, read: true, update: true, delete: true },
  appThemeSelector: { create: true, read: true, update: true, delete: true },
  customerInteraction: { create: true, read: true, update: true, delete: true },
  introScreen: { create: true, read: true, update: true, delete: true },
  muhurt: { create: true, read: true, update: true, delete: true },
  interestingFacts: { create: true, read: true, update: true, delete: true },
  reports: { create: false, read: true, update: false, delete: false },
  uiArrangement: { create: true, read: true, update: true, delete: true },
  advertisement: { create: true, read: true, update: true, delete: true },

  // Products Management
  products: { create: true, read: true, update: true, delete: true },
  productCategory: { create: true, read: true, update: true, delete: true },

  // Plan Management
  createSipPlan: { create: true, read: true, update: true, delete: true },
  allSipSubscribers: { create: false, read: true, update: true, delete: false },
  createDepositPlan: { create: true, read: true, update: true, delete: true },
  allSubscribers: { create: false, read: true, update: true, delete: false },

  // Gifts
  bulkGifts: { create: true, read: true, update: true, delete: true },
  bulkDiamondPoints: { create: true, read: true, update: true, delete: true },
  birthdayGift: { create: true, read: true, update: true, delete: true },
  registrationGift: { create: true, read: true, update: true, delete: true },

  // Customers
  allCustomers: { create: false, read: true, update: true, delete: false },
  kycVerification: { create: false, read: true, update: true, delete: false },
  inactiveCustomer: { create: false, read: true, update: false, delete: false },
  blockCustomer: { create: true, read: true, update: true, delete: false },

  // Admin
  admins: { create: true, read: true, update: true, delete: true },
  Permissions: { create: false, read: true, update: true, delete: false },

  // Store
  storemanagement: { create: true, read: true, update: true, delete: true },

  // Offers
  diamondPoints: { create: true, read: true, update: true, delete: true },
  vipConfig: { create: true, read: true, update: true, delete: true },
  flashoffers: { create: true, read: true, update: true, delete: true },
  metalRewards: { create: true, read: true, update: true, delete: true },
  referralBenefits: { create: true, read: true, update: true, delete: true },

  // Subscription
  subscriptionList: { create: false, read: true, update: true, delete: false },

  // Settings (for Jeweller)
  settingsMain: { create: true, read: true, update: true, delete: true },
  ticket: { create: true, read: true, update: true, delete: true },
  bookingBullion: { create: true, read: true, update: true, delete: false },
  bookingTransaction: { create: false, read: true, update: false, delete: false },
  bookingReport: { create: false, read: true, update: false, delete: false },
};

/**
 * Whether a CRUD permission is applicable for a given leaf key in Instalaxmi context.
 * true = permission is needed (show toggle); false = not needed (hide/—).
 *
 * Must include every Instalaxmi leaf key from SIDEBAR_PERMISSION_STRUCTURE.
 */
const INSTALAXMI_CRUD_OVERRIDES = {
  // Jeweller (sidebar item) - Instalaxmi context
  jeweller: { create: true, read: true, update: true, delete: true },

  // Subscriptions - Instalaxmi context
  subscriptionMain: { create: true, read: true, update: true, delete: true },
  couponlist: { create: true, read: true, update: true, delete: false },
  jewellerSubscriptionList: {
    create: false,
    read: true,
    update: false,
    delete: false,
  },

  // Settings - Instalaxmi context
  configuration: { create: true, read: true, update: true, delete: true },
  appConfig: { create: true, read: true, update: true, delete: true },

  // Special (Instalaxmi / Jeweller section visibility)
  instalaxmiPermission: { create: false, read: true, update: false, delete: false },
  jewellerPermission: { create: false, read: true, update: false, delete: false },
  training: { create: true, read: true, update: true, delete: true },
  dashboardInfo: { create: true, read: true, update: true, delete: false },
  leads: { create: true, read: true, update: true, delete: false },
};

const DEFAULT_CRUD = { create: true, read: true, update: true, delete: true };

/**
 * Returns CRUD config for a key based on whether it's an Instalaxmi or Jeweller key.
 */
function getCrudForKey(key) {
  const isInstalaxmiKey = INSTALAXMI_KEYS.has(key);
  const overrides = isInstalaxmiKey ? INSTALAXMI_CRUD_OVERRIDES : JEWELLER_CRUD_OVERRIDES;
  return overrides[key] ? { ...overrides[key] } : { ...DEFAULT_CRUD };
}

/**
 * Build PERMISSIONS_WITH_SUBTABS from sidebar structure so every tab/subtab has a permission.
 * For parents with children, children entries are leaf keys with CRUD applicability.
 */
function buildPermissionsFromSidebarStructure() {
  const out = {};
  for (const item of SIDEBAR_PERMISSION_STRUCTURE) {
    if (item.children && item.children.length > 0) {
      out[item.key] = {
        label: item.label,
        children: {},
      };
      for (const child of item.children) {
        out[item.key].children[child.key] = getCrudForKey(child.key);
      }
    } else {
      out[item.key] = getCrudForKey(item.key);
    }
  }
  return out;
}

export const PERMISSIONS_WITH_SUBTABS = buildPermissionsFromSidebarStructure();

const TYPE_TO_CRUD = {
  view: "read",
  create: "create",
  edit: "update",
  delete: "delete",
};

/**
 * Returns CRUD applicability config for a leaf permission key: { create, read, update, delete }.
 */
export function getCrudConfig(key) {
  const def = { create: false, read: false, update: false, delete: false };
  for (const [k, v] of Object.entries(PERMISSIONS_WITH_SUBTABS)) {
    if (k === key && typeof v.create === "boolean") {
      return { create: v.create, read: v.read, update: v.update, delete: v.delete };
    }
    if (v.children && v.children[key]) {
      const c = v.children[key];
      return {
        create: !!c.create,
        read: !!c.read,
        update: !!c.update,
        delete: !!c.delete,
      };
    }
  }
  return def;
}

/**
 * Whether the given permission type (view/create/edit/delete) is applicable for this key.
 */
export function isPermissionApplicable(key, type) {
  const crud = getCrudConfig(key);
  const crudKey = TYPE_TO_CRUD[type];
  return crudKey ? !!crud[crudKey] : false;
}

/**
 * Whether to show UI for this permission: type applicable AND user has it granted.
 */
export function canShowPermission(userPermissions, key, type) {
  if (!userPermissions || !key || !type) return false;
  return isPermissionApplicable(key, type) && !!userPermissions[key]?.[type];
}

/**
 * Returns leaf permission keys for a menu key (sidebar item).
 */
export function getLeafKeysForMenuKey(menuKey) {
  if (!menuKey) return [];
  const val = PERMISSIONS_WITH_SUBTABS[menuKey];
  if (!val) return [menuKey];
  if (typeof val.create === "boolean") return [menuKey];
  if (val.children) return Object.keys(val.children);
  return [menuKey];
}

/**
 * Whether the user has view access for a menu item (sidebar).
 */
export function hasViewAccessForMenu(userPermissions, menuKey) {
  if (!userPermissions) return false;
  const leafKeys = getLeafKeysForMenuKey(menuKey);
  return leafKeys.some((key) => canShowPermission(userPermissions, key, "view"));
}

/**
 * Default permissions for a new role: only permissions marked true in config.
 * Output is sparse per key (only true toggles present) because UI checks booleans.
 */
export function flattenDefaultPermissions() {
  const flat = {};
  const leafKeys = getLeafKeysFromStructure();
  for (const key of leafKeys) {
    const crud = getCrudConfig(key);
    flat[key] = {};
    if (crud.read) flat[key].view = true;
    if (crud.create) flat[key].create = true;
    if (crud.update) flat[key].edit = true;
    if (crud.delete) flat[key].delete = true;
  }
  return flat;
}

export const DEFAULT_FULL_PERMISSIONS = flattenDefaultPermissions();

/**
 * Returns all leaf permission keys (from sidebar + subtabs config).
 */
export function getAllPermissionKeys() {
  return getLeafKeysFromStructure();
}

/**
 * Returns permissions with all keys and all types (view, create, edit, delete) set to false.
 */
export function getEmptyPermissions() {
  const keys = getAllPermissionKeys();
  const out = {};
  keys.forEach((key) => {
    out[key] = { view: false, create: false, edit: false, delete: false };
  });
  return out;
}

/**
 * Builds the permission object in the format expected by API/backend:
 * each leaf key -> only { view, create, edit, delete } that are true (false values omitted).
 * Keys with no true permissions are omitted.
 */
export function buildPermissionsForApi(permissionsState) {
  const keys = getAllPermissionKeys();
  const out = {};
  for (const key of keys) {
    const p = permissionsState[key] || {};
    const entry = {};
    if (p.view) entry.view = true;
    if (p.create) entry.create = true;
    if (p.edit) entry.edit = true;
    if (p.delete) entry.delete = true;
    if (Object.keys(entry).length > 0) {
      out[key] = entry;
    }
  }
  return out;
}

export default DEFAULT_FULL_PERMISSIONS;

